import React, {
	ReactNode,
	useCallback,
	useEffect,
	useId,
	useContext,
	useImperativeHandle,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import {OverflowSet} from '../overflow-set';
import {useIsSsrDataPass} from '../../../lib/ssr-data-pass';
import {Keys, cls, removeArrayElementByIndex} from '../../utils';
import {
	LIST_ITEM_ATTR,
	ListContext,
	ListController,
	ListItemIndexContext,
	ListRowRefContext,
	ListNavigationMode,
	ListSelectAppearance,
	ListSelectMode,
	ListValue,
	ListWiringContext,
} from './list-context';
import {ListGroup, ListGroupProps} from './list-group';
import {ListItem} from './list-item';

import css from './list.module.css';

type ListElementProps = Omit<
	React.HTMLAttributes<HTMLUListElement | HTMLOListElement | HTMLDivElement> &
		Pick<React.OlHTMLAttributes<HTMLOListElement>, 'reversed' | 'start'>,
	'onSelect' | 'children'
>;

type Props = {
	children?: ReactNode;
	/** тег списка; по умолчанию ul при навигации по пунктам и div в остальных случаях */
	as?: 'ul' | 'ol' | 'div';
	/** режим выделения; без него пункты не выделяются */
	selectMode?: ListSelectMode;
	/** выделенные пункты (управляемый список) */
	selectedItems?: ListValue[];
	/** выделенные пункты на старте (неуправляемый список) */
	defaultSelectedItems?: ListValue[];
	/** выделение изменилось */
	onSelect?(values: ListValue[], event: React.SyntheticEvent): void;
	/** сюда всплывают действия пунктов: клик, Enter, Space */
	onItemAction?(value: ListValue | undefined, index: number, event: React.SyntheticEvent): void;
	/** чем показываем выделение: подсветкой кнопки (по умолчанию), галочками или полоской сбоку */
	selectAppearance?: ListSelectAppearance;
	/**
	 * Навигация с клавиатуры: items — стрелки ходят по пунктам,
	 * composite — плюс стрелки вправо-влево заходят внутрь пункта.
	 */
	navigationMode?: ListNavigationMode;
	/**
	 * Виртуальный фокус: настоящий фокус остаётся снаружи (например, в поле ввода),
	 * а активный пункт подсвечивается и объявляется через aria-activedescendant.
	 */
	virtualFocus?: boolean;
	/** управление виртуальным фокусом снаружи */
	controllerRef?: React.Ref<ListController>;
	/** активный пункт виртуального фокуса сменился; id — для aria-activedescendant */
	onActiveChange?(id: string | undefined, value: ListValue | undefined, index: number | null): void;
	/** стрелка за пределы списка при навигации по пунктам: вверх с первого или вниз с последнего */
	onNavigateOut?(edge: 'start' | 'end', event: React.KeyboardEvent): void;
	/**
	 * Рисовать только видимые пункты. Список сам становится прокручиваемым контейнером,
	 * поэтому высоту ему задаёт родитель.
	 */
	virtual?: boolean;
	/**
	 * Высота пункта при виртуализации до первых измерений, px. Дальше неотрисованные пункты
	 * считаются средней высоты уже измеренных пунктов своей группы.
	 */
	itemSize?: number;
	/** высота заголовка группы при виртуализации до первых измерений, px */
	groupTitleSize?: number;
	/** виртуализация включается, только когда пунктов не меньше этого числа; меньше — обычный список */
	virtualThreshold?: number;
	horizontal?: boolean;
	/** непоместившиеся пункты уезжают в поповер с тремя точками */
	overflow?: boolean;
	className?: string;
	forwardRef?: React.RefObject<HTMLElement | null>;
};

type AllProps = Props & ListElementProps;

type Row = {node: ReactNode; key: React.Key; index: number; value?: ListValue};
type Segment = {key: string; title?: ReactNode; className?: string; titleClassName?: string; rows: Row[]};

type Layout = {top: number; bodyTop: number; titleHeight: number; height: number; rows: number[]};

const OVERSCAN = 6;
const TITLE_GROUP = '#title';

const titleKey = (segment: Segment) => `${segment.key}/${TITLE_GROUP}`;
const rowKey = (segment: Segment, row: Row) => `${segment.key}/${String(row.key)}`;

/** первая строка, нижний край которой ниже offset */
const rowAt = (rows: number[], offset: number) => {
	let low = 0;
	let high = rows.length - 1;
	while (low < high) {
		const middle = (low + high) >> 1;
		if (rows[middle + 1] > offset) high = middle;
		else low = middle + 1;
	}
	return low;
};

const itemTagFor = (tag: string): 'li' | 'div' => (tag === 'ul' || tag === 'ol' ? 'li' : 'div');

const isElementOf = <P,>(node: ReactNode, type: React.FC<P>): node is React.ReactElement<P> =>
	React.isValidElement(node) && node.type === type;

function parseChildren(children: ReactNode) {
	const segments: Segment[] = [];
	const items: Array<{segment: number; row: number; value?: ListValue}> = [];
	let loose: Segment | null = null;

	const toRow = (node: ReactNode, position: number, segment: number): Row => {
		const key = (React.isValidElement(node) && node.key) || position;
		if (!isElementOf(node, ListItem)) return {node, key, index: -1};
		const value = node.props.value;
		items.push({segment, row: position, value});
		return {node, key, index: items.length - 1, value};
	};

	React.Children.toArray(children).forEach((child, position) => {
		if (isElementOf<ListGroupProps>(child, ListGroup)) {
			loose = null;
			const segment = segments.length;
			segments.push({
				key: String(child.key ?? position),
				title: child.props.title,
				className: child.props.className,
				titleClassName: child.props.titleClassName,
				rows: React.Children.toArray(child.props.children).map((node, row) => toRow(node, row, segment)),
			});
			return;
		}
		if (!loose) {
			loose = {key: `loose-${position}`, rows: []};
			segments.push(loose);
		}
		loose.rows.push(toRow(child, loose.rows.length, segments.length - 1));
	});

	return {segments, items};
}

function ListBody({
	children,
	as,
	selectMode,
	selectedItems,
	defaultSelectedItems,
	onSelect,
	onItemAction,
	selectAppearance,
	navigationMode,
	virtualFocus,
	controllerRef,
	onActiveChange,
	onNavigateOut,
	virtual,
	itemSize = 36,
	groupTitleSize = 32,
	virtualThreshold = 100,
	horizontal,
	overflow,
	className,
	forwardRef,
	role,
	tabIndex,
	onKeyDown,
	...divProps
}: AllProps) {
	const defaultRef = useRef<HTMLElement>(null);
	const listRef = (forwardRef || defaultRef) as React.RefObject<HTMLElement | null>;
	const listId = useId();

	const [ownSelected, setOwnSelected] = useState<ListValue[]>(defaultSelectedItems || []);
	const selected = selectedItems ?? ownSelected;

	const tag = as || (navigationMode === 'items' && !virtual ? 'ul' : 'div');
	const itemTag = itemTagFor(tag);

	const listRole =
		role ?? (navigationMode === 'composite' ? 'grid' : selectMode || virtualFocus ? 'listbox' : 'list');
	const itemRole =
		navigationMode === 'composite'
			? 'row'
			: selectMode || virtualFocus
			  ? 'option'
			  : itemTag === 'li'
			    ? undefined
			    : 'listitem';

	const appearance: ListSelectAppearance = selectAppearance || 'selected';

	const ssrDataPass = useIsSsrDataPass();
	const {segments, items} = useMemo(() => parseChildren(children), [children]);

	const sizes = useRef(new Map<string, number>());
	const estimates = useRef(new Map<string, number>());
	const [sizesVersion, setSizesVersion] = useState(0);
	const scrollCompensation = useRef(0);

	const layout = useMemo(() => {
		const average = (keys: string[]) => {
			let sum = 0;
			let count = 0;
			keys.forEach((key) => {
				const size = sizes.current.get(key);
				if (size === undefined) return;
				sum += size;
				count++;
			});
			return count ? {sum, count} : null;
		};

		const rowKeys = segments.map((segment) => segment.rows.map((row) => rowKey(segment, row)));
		const all = average(rowKeys.flat());
		const titles = average(segments.filter((segment) => segment.title !== undefined).map(titleKey));
		const titleEstimate = titles ? titles.sum / titles.count : groupTitleSize;
		estimates.current.set(TITLE_GROUP, titleEstimate);

		let offset = 0;
		return segments.map((segment, index): Layout => {
			const own = average(rowKeys[index]);
			const estimate = own ? own.sum / own.count : all ? all.sum / all.count : itemSize;
			estimates.current.set(segment.key, estimate);

			const titleHeight = segment.title !== undefined ? (sizes.current.get(titleKey(segment)) ?? titleEstimate) : 0;
			const rows = new Array<number>(segment.rows.length + 1);
			rows[0] = 0;
			rowKeys[index].forEach((key, position) => {
				rows[position + 1] = rows[position] + (sizes.current.get(key) ?? estimate);
			});
			const top = offset;
			const height = titleHeight + rows[segment.rows.length];
			offset += height;
			return {top, bodyTop: top + titleHeight, titleHeight, height, rows};
		});
	}, [segments, itemSize, groupTitleSize, sizesVersion]);

	const [viewport, setViewport] = useState({top: 0, height: 0});
	const windowed = !!virtual && items.length >= virtualThreshold;
	const positioned = windowed && viewport.height > 0;
	const positionedRef = useRef(positioned);
	positionedRef.current = positioned;

	const observed = useRef(new Map<string, HTMLElement>());
	const measureCallbacks = useRef(new Map<string, (element: HTMLElement | null) => void>());
	const resizeObserver = useRef<ResizeObserver | null>(null);

	const applySize = useCallback(
		(key: string, group: string, element: HTMLElement) => {
			const height = element.getBoundingClientRect().height;
			if (!height) return false;
			const previous = sizes.current.get(key);
			if (previous !== undefined && Math.abs(previous - height) < 0.5) return false;
			const list = listRef.current;
			if (positionedRef.current && list && element.getBoundingClientRect().bottom <= list.getBoundingClientRect().top) {
				scrollCompensation.current += height - (previous ?? estimates.current.get(group) ?? itemSize);
			}
			sizes.current.set(key, height);
			return true;
		},
		[listRef, itemSize],
	);

	const applySizeRef = useRef(applySize);
	applySizeRef.current = applySize;

	const measureRef = useCallback((key: string, group: string) => {
		let callback = measureCallbacks.current.get(key);
		if (!callback) {
			callback = (element) => {
				const previous = observed.current.get(key);
				if (previous && previous !== element) resizeObserver.current?.unobserve(previous);
				if (!element) {
					observed.current.delete(key);
					return;
				}
				observed.current.set(key, element);
				if (!resizeObserver.current) {
					resizeObserver.current = new ResizeObserver((entries) => {
						let changed = false;
						entries.forEach((entry) => {
							const target = entry.target as HTMLElement;
							const {measureKey, measureGroup} = target.dataset;
							if (measureKey && applySizeRef.current(measureKey, measureGroup ?? '', target)) changed = true;
						});
						if (changed) setSizesVersion((version) => version + 1);
					});
				}
				element.dataset.measureKey = key;
				element.dataset.measureGroup = group;
				resizeObserver.current.observe(element);
				if (applySizeRef.current(key, group, element)) setSizesVersion((version) => version + 1);
			};
			measureCallbacks.current.set(key, callback);
		}
		return callback;
	}, []);

	useEffect(() => () => resizeObserver.current?.disconnect(), []);

	useLayoutEffect(() => {
		const element = listRef.current;
		if (!element || !scrollCompensation.current) return;
		element.scrollTop += scrollCompensation.current;
		scrollCompensation.current = 0;
	}, [sizesVersion, listRef]);

	useEffect(() => {
		const element = listRef.current;
		if (!virtual || !element) return;
		let width = element.clientWidth;
		const update = () =>
			setViewport((current) =>
				current.top === element.scrollTop && current.height === element.clientHeight
					? current
					: {top: element.scrollTop, height: element.clientHeight},
			);
		const resize = () => {
			if (element.clientWidth !== width) {
				width = element.clientWidth;
				sizes.current.forEach((_, key) => {
					if (!observed.current.has(key)) sizes.current.delete(key);
				});
				setSizesVersion((version) => version + 1);
			}
			update();
		};
		update();
		element.addEventListener('scroll', update, {passive: true});
		const observer = new ResizeObserver(resize);
		observer.observe(element);
		return () => {
			element.removeEventListener('scroll', update);
			observer.disconnect();
		};
	}, [virtual, listRef]);

	const rowBox = useCallback(
		(index: number) => {
			const item = items[index];
			if (!item) return {top: 0, height: 0};
			const box = layout[item.segment];
			return {top: box.bodyTop + box.rows[item.row], height: box.rows[item.row + 1] - box.rows[item.row]};
		},
		[items, layout],
	);

	const scrollToIndex = useCallback(
		(index: number, align: 'nearest' | 'center' = 'nearest', behavior: ScrollBehavior = 'auto') => {
			const element = listRef.current;
			const item = items[index];
			if (!element || !item) return;

			if (!virtual) {
				element
					.querySelector(`[data-index="${index}"]`)
					?.scrollIntoView({block: align === 'center' ? 'center' : 'nearest', behavior});
				return;
			}

			const {top, height: size} = rowBox(index);
			const sticky = layout[item.segment].titleHeight;
			const height = element.clientHeight;
			let next = element.scrollTop;
			if (align === 'center') next = top - (height - size) / 2;
			else if (top - sticky < element.scrollTop) next = top - sticky;
			else if (top + size > element.scrollTop + height) next = top + size - height;
			if (next !== element.scrollTop) element.scrollTo({top: Math.max(0, next), behavior});
		},
		[listRef, items, virtual, rowBox, layout],
	);

	const isRowVisible = useCallback(
		(index: number) => {
			const element = listRef.current;
			if (!element) return false;
			const {top, height} = rowBox(index);
			return top >= element.scrollTop && top + height <= element.scrollTop + element.clientHeight;
		},
		[listRef, rowBox],
	);

	const isSelected = useCallback((value?: ListValue) => value !== undefined && selected.includes(value), [selected]);

	const toggle = useCallback(
		(value: ListValue | undefined, event: React.SyntheticEvent) => {
			if (!selectMode || value === undefined) return;

			const next =
				selectMode === 'multiple'
					? selected.includes(value)
						? removeArrayElementByIndex(selected, selected.indexOf(value))
						: [...selected, value]
					: selected.includes(value)
					  ? []
					  : [value];

			if (selectedItems === undefined) setOwnSelected(next);
			if (onSelect) onSelect(next, event);
		},
		[selectMode, selected, selectedItems, onSelect],
	);

	const itemAction = useCallback(
		(value: ListValue | undefined, index: number, event: React.SyntheticEvent) => {
			if (onItemAction) onItemAction(value, index, event);
		},
		[onItemAction],
	);

	const idFor = useCallback((index: number) => `${listId}-item-${index}`, [listId]);

	const [activeIndex, setActiveIndexState] = useState<number | null>(null);
	const [activeFocusVisible, setActiveFocusVisible] = useState(false);

	const setActiveIndex = useCallback(
		(index: number | null, focusVisible = true) => {
			setActiveIndexState(index);
			setActiveFocusVisible(index !== null && focusVisible);
			if (index !== null && focusVisible) scrollToIndex(index);
		},
		[scrollToIndex],
	);

	const selectedIndex = useCallback(() => {
		const index = items.findIndex((item) => item.value !== undefined && selected.includes(item.value));
		return index < 0 ? null : index;
	}, [items, selected]);

	const itemsSignature = items.map((item) => item.value).join(' ');
	useEffect(() => setActiveIndexState(null), [itemsSignature]);

	useEffect(() => {
		if (!virtualFocus) return;
		onActiveChange?.(
			activeIndex === null ? undefined : idFor(activeIndex),
			activeIndex === null ? undefined : items[activeIndex]?.value,
			activeIndex,
		);
	}, [virtualFocus, activeIndex]);

	const pendingFocus = useRef<number | null>(null);

	const focusIndex = useCallback(
		(index: number) => {
			pendingFocus.current = index;
			scrollToIndex(index);
			const element = listRef.current?.querySelector<HTMLElement>(`[data-index="${index}"]`);
			if (element) {
				(element.querySelector<HTMLElement>('a,button') || element).focus();
				pendingFocus.current = null;
			}
		},
		[scrollToIndex, listRef],
	);

	useLayoutEffect(() => {
		if (pendingFocus.current === null) return;
		const element = listRef.current?.querySelector<HTMLElement>(`[data-index="${pendingFocus.current}"]`);
		if (!element) return;
		(element.querySelector<HTMLElement>('a,button') || element).focus();
		pendingFocus.current = null;
	});

	useImperativeHandle(
		controllerRef,
		() => ({
			activeIndex: () => activeIndex,
			move: (delta) => {
				if (!items.length) return;
				const from = activeIndex ?? selectedIndex();
				const next = from === null ? (delta > 0 ? 0 : items.length - 1) : from + (activeIndex === null ? 0 : delta);
				setActiveIndex(Math.max(0, Math.min(items.length - 1, next)));
			},
			first: () => items.length && setActiveIndex(0),
			last: () => items.length && setActiveIndex(items.length - 1),
			clear: () => setActiveIndex(null),
			activate: () => {
				if (activeIndex === null) return false;
				const element = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
				const target = element?.querySelector<HTMLElement>('a,button') || element;
				if (!target) return false;
				target.click();
				return true;
			},
			focus: (index) => {
				if (!items.length) return;
				focusIndex(index ?? selectedIndex() ?? 0);
			},
		}),
		[activeIndex, items.length, setActiveIndex, listRef, selectedIndex, focusIndex],
	);

	const firstSelected = selected[0];
	const centeredOnce = useRef(false);
	useEffect(() => {
		if (!virtual || !viewport.height || firstSelected === undefined) return;
		const index = items.findIndex((item) => item.value === firstSelected);
		if (index < 0) return;
		if (!centeredOnce.current || !isRowVisible(index))
			scrollToIndex(index, 'center', centeredOnce.current ? 'smooth' : 'auto');
		centeredOnce.current = true;
	}, [virtual, !!viewport.height, firstSelected, itemsSignature]);

	const context = useMemo(
		() => ({
			selectMode,
			selectAppearance: appearance,
			navigationMode,
			virtualFocus,
			virtual,
			horizontal,
			itemTag,
			itemRole,
			isSelected,
			toggle,
			itemAction,
			activeIndex,
			activeFocusVisible,
			setActiveIndex,
			idFor,
			indexOf: (element: HTMLElement | null) =>
				element
					? Array.from(listRef.current?.querySelectorAll<HTMLElement>(`[${LIST_ITEM_ATTR}]`) || []).indexOf(
							element,
					  )
					: -1,
			size: () => items.length,
		}),
		[
			selectMode,
			appearance,
			navigationMode,
			virtualFocus,
			virtual,
			horizontal,
			itemTag,
			itemRole,
			isSelected,
			toggle,
			itemAction,
			activeIndex,
			activeFocusVisible,
			setActiveIndex,
			idFor,
			listRef,
			items.length,
		],
	);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLUListElement | HTMLOListElement | HTMLDivElement>) => {
			if (onKeyDown) onKeyDown(event);
			if (!navigationMode || virtualFocus || event.defaultPrevented || !items.length) return;

			const active = document.activeElement as HTMLElement | null;
			const current = active?.closest<HTMLElement>('[data-index]');
			const position = current ? Number(current.dataset.index) : -1;
			const button = current?.querySelector('a,button');
			const inside = !!current && !!active && active !== button && active !== current;

			const innerTargets = (item: HTMLElement) =>
				Array.from(item.querySelectorAll<HTMLElement>('a,button,input,select,textarea,[tabindex]')).filter(
					(element) => element !== button,
				);

			switch (event.keyCode) {
				case Keys.DOWN_ARROW:
					if (position < 0) focusIndex(selectedIndex() ?? 0);
					else if (position === items.length - 1 && onNavigateOut) onNavigateOut('end', event);
					else focusIndex(Math.min(position + 1, items.length - 1));
					event.preventDefault();
					break;
				case Keys.UP_ARROW:
					if (position < 0) focusIndex(selectedIndex() ?? items.length - 1);
					else if (position === 0 && onNavigateOut) onNavigateOut('start', event);
					else focusIndex(Math.max(position - 1, 0));
					event.preventDefault();
					break;
				case Keys.HOME:
					focusIndex(0);
					event.preventDefault();
					break;
				case Keys.END:
					focusIndex(items.length - 1);
					event.preventDefault();
					break;
				case Keys.RIGHT_ARROW: {
					if (navigationMode !== 'composite' || !current) break;
					const targets = innerTargets(current);
					const next = inside && active ? targets[targets.indexOf(active) + 1] : targets[0];
					if (next) {
						next.focus();
						event.preventDefault();
					}
					break;
				}
				case Keys.LEFT_ARROW: {
					if (navigationMode !== 'composite' || !current) break;
					const targets = innerTargets(current);
					const previous = inside && active ? targets[targets.indexOf(active) - 1] : undefined;
					if (previous) previous.focus();
					else focusIndex(position);
					event.preventDefault();
					break;
				}
				case Keys.ESCAPE:
					if (inside && current) focusIndex(position);
					else listRef.current?.focus();
					event.preventDefault();
					break;
			}
		},
		[onKeyDown, navigationMode, virtualFocus, items.length, focusIndex, listRef, selectedIndex, onNavigateOut],
	);

	if (overflow) {
		return (
			<OverflowSet
				items={React.Children.toArray(children) as Array<React.JSX.Element>}
				className={cls(css.list, horizontal ? css.horizontal : css.vertical, className)}
				forwardRef={listRef as React.RefObject<HTMLDivElement | null>}
			/>
		);
	}

	const renderRow = (row: Row, style?: React.CSSProperties, measure?: (element: HTMLElement | null) => void) => {
		const node =
			style && React.isValidElement<{style?: React.CSSProperties}>(row.node) && row.index >= 0
				? React.cloneElement(row.node, {style: {...row.node.props.style, ...style}})
				: row.node;
		if (row.index >= 0) {
			return (
				<ListItemIndexContext.Provider key={row.key} value={row.index}>
					<ListRowRefContext.Provider value={measure ?? null}>{node}</ListRowRefContext.Provider>
				</ListItemIndexContext.Provider>
			);
		}
		return style || measure ? (
			<div key={row.key} style={style} ref={measure}>
				{node}
			</div>
		) : (
			<React.Fragment key={row.key}>{node}</React.Fragment>
		);
	};

	const BodyTag = (itemTag === 'li' ? 'ul' : 'div') as 'div';

	const renderSegment = (segment: Segment, index: number) => {
		const box = layout[index];
		const titleId = `${listId}-group-${index}`;
		const grouped = segment.title !== undefined;

		let rows: ReactNode;
		let bodyStyle: React.CSSProperties | undefined;

		if (positioned) {
			let from = 0;
			let to = 0;
			const bodyHeight = box.rows[segment.rows.length];
			const start = viewport.top - box.bodyTop;
			const end = start + viewport.height;
			if (end > 0 && start < bodyHeight) {
				from = Math.max(0, rowAt(box.rows, Math.max(0, start)) - OVERSCAN);
				to = Math.min(segment.rows.length, rowAt(box.rows, end) + 1 + OVERSCAN);
			}
			rows = segment.rows
				.slice(from, Math.max(from, to))
				.map((row, offset) =>
					renderRow(
						row,
						{position: 'absolute', top: box.rows[from + offset], left: 0, right: 0},
						measureRef(rowKey(segment, row), segment.key),
					),
				);
			bodyStyle = {position: 'relative', height: bodyHeight};
		} else if (virtual && ssrDataPass) {
			rows = null;
		} else if (virtual) {
			rows = segment.rows.map((row) => renderRow(row, undefined, measureRef(rowKey(segment, row), segment.key)));
		} else {
			rows = segment.rows.map((row) => renderRow(row));
		}

		if (!grouped && !virtual) return <React.Fragment key={segment.key}>{rows}</React.Fragment>;

		const WrapperTag = itemTag as 'div';
		const nested = grouped && listRole === 'list';

		let wrapperRole: string | undefined = 'presentation';
		let bodyRole: string | undefined = itemTag === 'li' ? 'presentation' : undefined;
		if (nested) {
			wrapperRole = itemTag === 'li' ? undefined : 'listitem';
			bodyRole = itemTag === 'li' ? undefined : 'list';
		} else if (grouped && itemTag === 'div') {
			wrapperRole = 'group';
		} else if (grouped) {
			bodyRole = 'group';
		}
		const labelledOnBody = grouped && (nested || itemTag === 'li');

		return (
			<WrapperTag
				key={segment.key}
				role={wrapperRole}
				aria-labelledby={grouped && !labelledOnBody ? titleId : undefined}
				className={cls(css.group, segment.className)}
				style={positioned ? {height: box.height} : undefined}
			>
				{grouped && (
					<div
						id={titleId}
						role={nested ? undefined : 'presentation'}
						className={cls(css.groupTitle, segment.titleClassName)}
						ref={virtual ? measureRef(titleKey(segment), TITLE_GROUP) : undefined}
					>
						{segment.title}
					</div>
				)}
				<BodyTag
					role={bodyRole}
					aria-labelledby={labelledOnBody ? titleId : undefined}
					className={css.groupItems}
					style={bodyStyle}
				>
					{rows}
				</BodyTag>
			</WrapperTag>
		);
	};

	const Tag = tag as 'ul';

	return (
		<Tag
			{...divProps}
			ref={listRef as React.RefObject<HTMLUListElement | null>}
			className={cls(
				css.list,
				horizontal ? css.horizontal : css.vertical,
				virtual && css.virtual,
				virtualFocus && css.virtualFocus,
				className,
			)}
			style={positioned ? {...divProps.style, overflowAnchor: 'none'} : divProps.style}
			role={listRole}
			tabIndex={tabIndex ?? (navigationMode && !virtualFocus ? 0 : undefined)}
			onKeyDown={handleKeyDown}
		>
			<ListContext.Provider value={context}>{segments.map(renderSegment)}</ListContext.Provider>
		</Tag>
	);
}

/**
 * Список внутри выпадашки подхватывает от неё управление с клавиатуры, даже если лежит не прямо в ней,
 * а внутри своего компонента. Вложенным в него спискам управление уже не передаётся.
 */
export function List(props: AllProps) {
	const wiring = useContext(ListWiringContext);
	if (!wiring) return <ListBody {...props} />;
	return (
		<ListWiringContext.Provider value={null}>
			<ListBody
				{...props}
				navigationMode={props.navigationMode ?? wiring.navigationMode}
				virtualFocus={props.virtualFocus ?? wiring.virtualFocus}
				controllerRef={props.controllerRef ?? wiring.controllerRef}
				onActiveChange={props.onActiveChange ?? wiring.onActiveChange}
			/>
		</ListWiringContext.Provider>
	);
}
